// ------------------------------------------------------------------------------
// <copyright file="useScenarioRunner.js" company="C2HL">
//     Copyright (c) 2024 C2HL. All rights reserved.
// </copyright>
// ------------------------------------------------------------------------------

/**
 * useScenarioRunner — Scenario step execution engine.
 *
 * Executes ordered steps in a scenario definition, resolving {varName} tokens
 * from three sources (priority order):
 *   1. User-supplied overrides (entered in the variable override form)
 *   2. Extractors: values pulled from prior step responses
 *   3. Scenario variable defaults (may be string or () => string)
 *
 * Auth header injection per step:
 *   - 'none'         → no Authorization header
 *   - 'jwt'          → Authorization: Bearer {jwt} (must be in extractedVars)
 *   - 'function-key' → x-functions-key: {functionKey} (passed to the hook)
 */

import { useState, useCallback } from 'react';
import { trackEvent, trackException } from '../services/appInsightsService';

// ---------------------------------------------------------------------------
// Variable resolution
// ---------------------------------------------------------------------------

/**
 * Resolve all scenario variable defaults into concrete string values.
 * Function-typed variables are called at execution time.
 *
 * @param {object} variables - Scenario variables map (name -> string | () => string)
 * @returns {object} Resolved values (name -> string)
 */
function resolveDefaults(variables) {
  const resolved = {};
  Object.entries(variables || {}).forEach(([key, value]) => {
    resolved[key] = typeof value === 'function' ? value() : value;
  });
  return resolved;
}

/**
 * Build the merged variable scope for a step.
 * Priority: user overrides > extractors > scenario defaults.
 *
 * @param {object} defaults - Resolved scenario variable defaults
 * @param {object} extractors - Values extracted from prior step responses
 * @param {object} overrides - User-supplied variable overrides
 * @returns {object} Merged variable scope
 */
function buildScope(defaults, extractors, overrides) {
  return { ...defaults, ...extractors, ...overrides };
}

/**
 * Substitute all {varName} tokens in a string.
 *
 * @param {string} template - String possibly containing {varName} tokens
 * @param {object} scope - Variable name-to-value map
 * @returns {string} String with tokens replaced
 */
function interpolate(template, scope) {
  if (typeof template !== 'string') return template;
  return template.replace(/\{(\w+)\}/g, (_, key) => {
    return scope[key] !== undefined ? scope[key] : `{${key}}`;
  });
}

/**
 * Recursively substitute {varName} tokens in an object/array/string.
 *
 * @param {any} value - Value to interpolate
 * @param {object} scope - Variable name-to-value map
 * @returns {any} Value with all string tokens replaced
 */
function resolveValue(value, scope) {
  if (typeof value === 'string') {
    // A value that is exactly one token keeps the variable's own type, so an extracted number
    // (e.g. billingSchemaVersion) is sent as a JSON number rather than a string the server rejects.
    const whole = /^\{(\w+)\}$/.exec(value);
    if (whole && scope[whole[1]] !== undefined) return scope[whole[1]];
    return interpolate(value, scope);
  }
  if (Array.isArray(value)) return value.map(item => resolveValue(item, scope));
  if (value !== null && typeof value === 'object') {
    const resolved = {};
    Object.entries(value).forEach(([k, v]) => {
      resolved[k] = resolveValue(v, scope);
    });
    return resolved;
  }
  return value;
}

// ---------------------------------------------------------------------------
// Step execution
// ---------------------------------------------------------------------------

/**
 * Build request headers for a step based on its auth type.
 *
 * @param {string} auth - Auth type: 'none' | 'jwt' | 'function-key'
 * @param {string} functionKey - Function key for 'function-key' auth
 * @param {object} scope - Variable scope (contains 'jwt' if extracted)
 * @returns {object} Headers object
 */
function buildHeaders(auth, functionKey, scope) {
  const headers = { 'Content-Type': 'application/json' };
  if (auth === 'jwt') {
    const jwt = scope['jwt'];
    if (jwt) headers['Authorization'] = `Bearer ${jwt}`;
  } else if (auth === 'function-key') {
    headers['x-functions-key'] = functionKey;
  }
  return headers;
}

/**
 * Execute a single step and return the result.
 *
 * @param {object} step - Step definition
 * @param {string} baseUrl - Base API URL (e.g. 'http://localhost:7071/api')
 * @param {string} functionKey - Function key for 'function-key' auth steps
 * @param {object} scope - Resolved variable scope for this step
 * @returns {Promise<object>} Step result: { status, response, durationMs, error? }
 */
async function executeStep(step, baseUrl, functionKey, scope) {
  // Resolve the path (substitute {varName} tokens)
  const resolvedPath = interpolate(step.path, scope);
  const url = `${baseUrl}${resolvedPath}`;

  const headers = buildHeaders(step.auth, functionKey, scope);

  const options = {
    method: step.method,
    headers,
  };

  if (step.body && step.method !== 'GET' && step.method !== 'DELETE') {
    options.body = JSON.stringify(resolveValue(step.body, scope));
  }

  const start = Date.now();
  let response;
  let responseData;
  let error;

  try {
    response = await fetch(url, options);
    const durationMs = Date.now() - start;

    try {
      responseData = await response.json();
    } catch {
      responseData = null;
    }

    const expectedStatuses = step.expect?.status || [];
    const passed = expectedStatuses.includes(response.status);

    return {
      status: passed ? 'pass' : 'fail',
      httpStatus: response.status,
      response: responseData,
      durationMs,
    };
  } catch (err) {
    const durationMs = Date.now() - start;
    error = err instanceof Error ? err.message : String(err);
    return {
      status: 'fail',
      httpStatus: null,
      response: null,
      durationMs,
      error,
    };
  }
}

/**
 * Extract values from a step response using extractor definitions.
 *
 * @param {object} extractors - Map of variableName -> responseFieldPath (dot notation)
 * @param {object} responseData - Parsed JSON response body
 * @returns {object} Extracted variable values
 */
function extractFromResponse(extractors, responseData) {
  if (!extractors || !responseData) return {};
  const extracted = {};
  Object.entries(extractors).forEach(([varName, fieldPath]) => {
    // Support dot notation: 'data.tenantId' -> responseData.data.tenantId
    const parts = fieldPath.split('.');
    let value = responseData;
    for (const part of parts) {
      if (value === null || value === undefined) break;
      value = value[part];
    }
    if (value !== undefined && value !== null) {
      extracted[varName] = value;
    }
  });
  return extracted;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * useScenarioRunner — execution engine hook for running scenario steps.
 *
 * @param {string} baseUrl - Base API URL for all steps
 * @param {string} functionKey - x-functions-key for 'function-key' auth steps
 * @returns {{
 *   running: boolean,
 *   currentStep: string|null,
 *   results: object,
 *   extractedVars: object,
 *   runScenario: Function,
 * }}
 */
function useScenarioRunner(baseUrl, functionKey) {
  const [running, setRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(null);
  const [results, setResults] = useState({});
  const [extractedVars, setExtractedVars] = useState({});

  /**
   * Execute all steps in a scenario sequentially.
   *
   * @param {object} scenario - Scenario definition from SCENARIO_CATEGORIES
   * @param {object} overrides - User-supplied variable overrides
   */
  const runScenario = useCallback(async (scenario, overrides) => {
    setRunning(true);
    setCurrentStep(null);
    setResults({});
    setExtractedVars({});

    const defaults = resolveDefaults(scenario.variables);
    const accumulated = {}; // extractors accumulated across steps

    try {
      trackEvent('ScenarioTesting.Run', {
        scenarioId: scenario.id,
        stepCount: scenario.steps.length,
      });

      for (const step of scenario.steps) {
        setCurrentStep(step.id);

        const scope = buildScope(defaults, accumulated, overrides);
        const stepResult = await executeStep(step, baseUrl, functionKey, scope);

        // Extract values from this step's response for subsequent steps
        if (step.extractors && stepResult.response) {
          const extracted = extractFromResponse(step.extractors, stepResult.response);
          Object.assign(accumulated, extracted);
          setExtractedVars({ ...accumulated });
        }

        setResults(prev => ({ ...prev, [step.id]: stepResult }));

        if (stepResult.status === 'fail') {
          trackEvent('ScenarioTesting.StepFailed', {
            scenarioId: scenario.id,
            stepId: step.id,
            httpStatus: String(stepResult.httpStatus ?? 'network-error'),
          });
          // Halt: subsequent steps depend on outputs from this step (e.g. tenantId,
          // jwt). Continuing past a failure would create an inconsistent Cosmos state
          // and make debugging harder. Break and let the caller decide to re-run.
          break;
        }
      }

      trackEvent('ScenarioTesting.Complete', { scenarioId: scenario.id });
    } catch (err) {
      trackException(err instanceof Error ? err : new Error(String(err)), {
        scenarioId: scenario.id,
        errorType: 'ScenarioRunner_UnexpectedError',
      });
    } finally {
      setCurrentStep(null);
      setRunning(false);
    }
  }, [baseUrl, functionKey]);

  return { running, currentStep, results, extractedVars, runScenario };
}

export default useScenarioRunner;
